import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { queryClient } from './services/queryClient';
import { AuthProvider } from './services/authContext';
import { ModulesProvider } from './services/modulesContext';
import { FlagsProvider } from './services/flagsContext';
import { ThemeProvider } from './services/themeContext';
import { PageErrorFallback } from './components/common/ErrorBoundary';
import './index.css';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    enabled: import.meta.env.PROD,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // F-1309: strip query strings from HTTP breadcrumb URLs so PII-bearing
    // query params (email, phone, name, etc.) are never sent to Sentry.
    // The URL path is preserved for debugging; only the ?queryString part
    // is removed.
    beforeBreadcrumb(crumb) {
      if (crumb.category === 'fetch' || crumb.category === 'xhr') {
        const url = crumb.data?.url;
        if (typeof url === 'string') {
          try {
            const parsed = new URL(url);
            parsed.search = '';
            crumb.data = { ...crumb.data, url: parsed.toString() };
          } catch {
            // Relative or unparseable URL — strip query string manually.
            const qIdx = url.indexOf('?');
            if (qIdx !== -1) {
              crumb.data = { ...crumb.data, url: url.slice(0, qIdx) };
            }
          }
        }
      }
      return crumb;
    },
    beforeSend(event) {
      // Filter out browser extension JSON-RPC errors (e.g. MetaMask)
      const msg = event.exception?.values?.[0]?.value ?? '';
      if (msg.includes('JSON-RPC') || msg.includes('-32603')) return null;
      const serialized = (event.extra as Record<string, unknown>)?.__serialized__ as Record<string, unknown> | undefined;
      if (serialized?.code === -32603 || String(serialized?.message ?? '').includes('JSON-RPC')) return null;

      // Enrich synthetic unhandled rejection events with structured data
      const mechanism = event.exception?.values?.[0]?.mechanism;
      if (mechanism?.synthetic && mechanism?.type?.includes('onunhandledrejection')) {
        // Tag for easier filtering in Sentry
        event.tags = { ...event.tags, 'error.synthetic': 'true' };
      }

      // Strip query strings from the top-level request URL so PII-bearing
      // query params (email, name, token, etc.) are never sent in error
      // reports. Mirrors the beforeBreadcrumb scrub above — both paths must
      // agree or PII leaks through whichever one is missing the strip.
      const reqUrl = event.request?.url;
      if (reqUrl) {
        try {
          const parsed = new URL(reqUrl);
          parsed.search = '';
          event.request = { ...event.request, url: parsed.toString() };
        } catch {
          // Unparseable URL — strip manually.
          const qIdx = reqUrl.indexOf('?');
          if (qIdx !== -1) {
            event.request = { ...event.request, url: reqUrl.slice(0, qIdx) };
          }
        }
      }

      return event;
    },
  });
}

// F20-001: wrap the SPA in Sentry.ErrorBoundary so uncaught render errors
// surface a recovery UI (PageErrorFallback) and get reported to Sentry,
// instead of unmounting the whole tree into a blank page.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <PageErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetErrorBoundary={resetError}
        />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ModulesProvider>
              <FlagsProvider>
                {/* MKS-493 — inside AuthProvider: the theme is read from the
                    signed-in user's preferences. */}
                <ThemeProvider>
                  <App />
                </ThemeProvider>
              </FlagsProvider>
            </ModulesProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
